import { KeyValuePipe } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Title } from '@angular/platform-browser';
import { firstValueFrom } from 'rxjs';

import {
  EErrorType,
  ErrorPageComponent,
} from '../../components/error-page/error-page.component';
import { LoadingComponent } from '../../components/loading/loading.component';
import { OtpInfoCardComponent } from '../../components/otp-info-card/otp-info-card.component';
import { SoloNumerosDirective } from '../../directives/SoloNumerosDirectives';
import { TokenService } from '../../services/token.service';
import { VerificaContactoService } from '../../services/validador-contacto.service';
import { isValidField } from '../../utils/forms/errorValidator';

@Component({
  selector: 'otp-formulario',
  imports: [
    ReactiveFormsModule,
    SoloNumerosDirective,
    KeyValuePipe,
    OtpInfoCardComponent,
    LoadingComponent,
    ErrorPageComponent,
  ],
  templateUrl: './otp-formulario.component.html',
  styleUrl: './otp-formulario.component.css',
})
export class OtpFormularioComponent implements OnInit {
  public readonly tokenService = inject(TokenService);
  private readonly validadorContactoService = inject(VerificaContactoService);
  private readonly fb = inject(FormBuilder);
  private readonly titleService = inject(Title);

  private readonly timeOut = 300;

  public readonly intentosActuales = signal(10);

  public readonly errorOtp = signal(false);

  public isCargando = signal(false);

  public errorValidator = isValidField;

  public correoCliente = this.tokenService.contactData?.datos?.correo ?? '';
  public telefonoCliente = this.tokenService.contactData?.datos?.telefono ?? '';

  get contactData() {
    return this.tokenService.contactData;
  }

  ngOnInit(): void {
    // Restaurar el título original
    this.titleService.setTitle('Clave dinámica');
  }

  get appState() {
    return this.tokenService.appState;
  }

  get isFormValid() {
    const formValues = this.otpForm.value;
    const allFieldsFilled = [
      formValues.otp1,
      formValues.otp2,
      formValues.otp3,
      formValues.otp4,
      formValues.otp5,
      formValues.otp6,
    ].every((value) => value !== null && value !== undefined && value !== '');

    return allFieldsFilled && this.otpForm.valid;
  }

  onRetryError(): void {
    this.intentosActuales.set(0);
    this.errorOtp.set(false);
    this.tokenService.setOtpState();
  }

  public otpForm = this.fb.group({
    otp1: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
    otp2: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
    otp3: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
    otp4: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
    otp5: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
    otp6: ['', [Validators.required, Validators.pattern(/^\d+$/)]],
  });

  public async onSubmit() {
    if (!this.otpForm.valid) {
      this.otpForm.markAllAsTouched();
      return;
    }

    const formValues = this.otpForm.value;
    const otpValue = [
      formValues.otp1 || '',
      formValues.otp2 || '',
      formValues.otp3 || '',
      formValues.otp4 || '',
      formValues.otp5 || '',
      formValues.otp6 || '',
    ].join('');

    this.isCargando.set(true);

    try {
      const response = await firstValueFrom(
        this.validadorContactoService.validarCodigo(otpValue)
      );

      this.isCargando.set(false);

      if (response.datos.codigoValido) {
        if (response.datos.redirectUrl) {
          this.tokenService.setRedirectUrl(response.datos.redirectUrl);
          this.tokenService.clearAll();
          window.location.href = response.datos.redirectUrl;
        } else {
          this.tokenService.setSuccessState();
        }
      } else {
        this.intentosActuales.update(() => response.datos.intentosRestantes);
        this.manejarReintentos(response.datos.redirectUrl);
      }
    } catch (error) {
      console.error('Error en la validación del código:', error);
      this.isCargando.set(false);
      this.tokenService.setErrorState(EErrorType.NOT_FOUND);
    }
  }

  public onInputChange(event: Event, currentIndex: number) {
    const value = (event.target as HTMLInputElement).value;

    if (this.errorOtp()) {
      this.errorOtp.set(false);
    }

    if (value && currentIndex < 5) {
      const inputs = document
        .querySelector('.otp-container')
        ?.querySelectorAll('input');

      if (!inputs) {
        return;
      }

      const nextInput = inputs[currentIndex + 1];
      if (nextInput) {
        nextInput.focus();
      }
    }
  }

  public onKeyDown(event: KeyboardEvent, currentIndex: number) {
    if (
      event.key === 'Backspace' &&
      event.currentTarget &&
      !(event.currentTarget as HTMLInputElement).value &&
      currentIndex > 0
    ) {
      const inputs = document
        .querySelector('.otp-container')
        ?.querySelectorAll('input');

      if (!inputs) {
        return;
      }

      const prevInput = inputs[currentIndex - 1];
      if (prevInput) {
        prevInput.focus();
      }
    }
  }

  public onPaste(event: ClipboardEvent) {
    event.preventDefault();

    if (this.errorOtp()) {
      this.errorOtp.set(false);
    }

    const pastedData = event.clipboardData?.getData('text') || '';
    const numbers = pastedData.replace(/\D/g, '');

    if (!numbers) {
      return;
    }

    const otpFields = ['otp1', 'otp2', 'otp3', 'otp4', 'otp5', 'otp6'];
    otpFields.forEach((fieldName, index) => {
      const value = numbers[index] || '';
      this.otpForm.get(fieldName)?.setValue(value);
    });

    const inputs = document
      .querySelector('.otp-container')
      ?.querySelectorAll('input');

    if (!inputs) {
      return;
    }

    inputs.forEach((input) => {
      input.setAttribute('type', 'text');
    });

    for (let i = 0; i < inputs.length; i++) {
      const input = inputs[i];
      input.value = numbers[i] || '';
    }

    const lastInputWithValueIndex = Array.from(inputs).findIndex(
      (input) => !input.value
    );

    if (
      lastInputWithValueIndex !== -1 &&
      lastInputWithValueIndex < inputs.length
    ) {
      setTimeout(() => {
        inputs.forEach((input) => {
          if (!input.value) {
            return;
          }
          input.setAttribute('type', 'password');
        });
      }, this.timeOut);

      inputs[lastInputWithValueIndex].focus();

      return;
    }

    setTimeout(() => {
      inputs.forEach((input) => {
        input.setAttribute('type', 'password');
      });
    }, this.timeOut);

    const lastInput = inputs
      ? (Array.from(inputs).at(-1) as HTMLInputElement)
      : null;

    lastInput?.focus();
  }

  public onFocus(event: FocusEvent) {
    const input = event.target as HTMLInputElement;

    input.select();
    input.setAttribute('type', 'text');
  }

  public onBlur(event: FocusEvent) {
    setTimeout(() => {
      const input = event.target as HTMLInputElement;
      input.setAttribute('type', 'password');
    }, this.timeOut);
  }

  private manejarReintentos(url?: string) {
    this.errorOtp.set(true);
    this.otpForm.reset();
    if (url) {
      this.tokenService.setRedirectUrl(url);
      this.tokenService.clearAll();
      window.location.href = url;
    }
  }
}
